// utils/helpers.jsx
import React from "react";

/** Normalise a measurement label to a flat key: "Pant Length" → "pantlength" */
export const mKeyNorm = (label) => label.toLowerCase().replace(/\s+/g, "");

/** Format a date string (YYYY-MM-DD) to "01 Jan 2024" */
export function fmt(date) {
  if (!date) return "—";
  try {
    return new Date(date + "T00:00:00").toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return date;
  }
}

/** Map a status / work-type string to a CSS badge class */
const BADGE_CLASS = {
  Completed:     "badge-green",
  "In Progress": "badge-orange",
  Pending:       "badge-red",
  Active:        "badge-green",
  Busy:          "badge-orange",
  Inactive:      "badge-red",
  Both:          "badge-gold",
  Pant:          "badge-blue",
  Shirt:         "badge-purple",
};

export function StatusBadge({ status }) {
  const cls = BADGE_CLASS[status] || "badge-blue";
  return (
    <span className={`badge ${cls}`}>
      <span className="badge-dot" />
      {status}
    </span>
  );
}

/** Payment modes offered by the Payment (karigar payout) and Receipt (order) forms. */
export const PAYMENT_MODES = ["Cash", "UPI", "Card", "Cheque"];

/** Today's date as YYYY-MM-DD (the format the PHP backend + fmt() expect). */
export function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Read an image File and downscale it so its longest edge is <= maxSize, resolving the
 * canvas it was drawn into. Shared by the data-URL and Blob variants below.
 */
function drawScaledToCanvas(file, maxSize) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file"));
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("Read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Downscale an image File and return a base64 JPEG data URL. Kept for anywhere an
 * inline image is genuinely wanted; prefer resizeImageToBlob() + uploadAPI.image()
 * for anything that gets persisted.
 */
export function resizeImageToDataURL(file, maxSize = 512, quality = 0.8) {
  return drawScaledToCanvas(file, maxSize).then((c) => c.toDataURL("image/jpeg", quality));
}

/**
 * Downscale an image File and return a JPEG Blob ready to POST as multipart/form-data.
 * Downscaling client-side keeps uploads far below any PHP upload_max_filesize /
 * post_max_size limit, so a raw multi-MB phone photo can't produce an HTTP 413.
 */
export function resizeImageToBlob(file, maxSize = 512, quality = 0.8) {
  return drawScaledToCanvas(file, maxSize).then(
    (c) =>
      new Promise((resolve, reject) => {
        c.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Encode failed"))),
          "image/jpeg",
          quality
        );
      })
  );
}
